import { FC, useState, useCallback } from 'react';
import Button from 'v3/common/components/Button';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';

type AlgoverseModalComponentProps = {
  isOpen: boolean;
  renderTitle: JSX.Element | string;
  renderBodyComponent: React.ReactNode | JSX.Element | string;
  renderFooterComponent?: React.ReactNode | JSX.Element | string;

  size?: 'sm' | 'lg' | 'xl'; //use undefined for default size (md) "md" is not a valid value in current React-Bootstrap.
  className?: string;
  backdrop?: boolean | 'static';
  centered?: boolean;
  closeButton?: boolean;

  okBtnTitle?: string;
  cancelBtnTitle?: string;
  isOkBtnDisabled?: boolean;
  okBtnClassName?: string;
  cancelBtnClassName?: string;

  onClose?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onSaveid?: string;

  shouldConfirmClose?: boolean | (() => boolean);
  confirmMessage?: string;
};

const AlgoverseModalComponent: FC<AlgoverseModalComponentProps> = (props) => {
  const {
    isOpen,
    renderTitle,
    renderBodyComponent,
    renderFooterComponent = null,
    size = undefined,
    className = '',
    backdrop = true,
    centered = true,
    closeButton = true,
    onClose,
    shouldConfirmClose = false,
    confirmMessage = 'Are you sure you want to close without saving?',
  } = props;

  const [isConfirming, setIsConfirming] = useState(false);

  const combinedClassName = classNames('algoverse-modal__wrapper', className, {
    open: isOpen,
  });

  const handleClose = useCallback(() => {
    const needsConfirm = typeof shouldConfirmClose === 'function' ? shouldConfirmClose() : shouldConfirmClose;
    if (needsConfirm) setIsConfirming(true);
    else onClose?.();
  }, [shouldConfirmClose, onClose]);

  const confirmClose = () => {
    setIsConfirming(false);
    onClose?.();
  };

  const cancelClose = () => setIsConfirming(false);

  const RenderCloseConfirmation = () => (
    <>
      <Modal.Header>
        <strong>Confirmation</strong>
      </Modal.Header>
      <Modal.Body className="text-center">{confirmMessage}</Modal.Body>
      <Modal.Footer>
        <Button className="outline-secondary" onClick={confirmClose}>
          Yes
        </Button>
        <Button className="primary" onClick={cancelClose}>
          No
        </Button>
      </Modal.Footer>
    </>
  );

  if (!isOpen) return null;

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      size={isConfirming ? undefined : size}
      backdrop={backdrop}
      centered={centered}
      className="algoverse-modal"
    >
      <div className={combinedClassName}>
        {isConfirming ? (
          <RenderCloseConfirmation />
        ) : (
          <>
            {renderTitle && (
              <Modal.Header className="algoverse-modal__header" closeButton={closeButton && !!onClose}>
                <Modal.Title className="algoverse-modal__title">{renderTitle}</Modal.Title>
              </Modal.Header>
            )}
            {renderBodyComponent && (
              <Modal.Body className="algoverse-modal__body">{renderBodyComponent}</Modal.Body>
            )}
            {renderFooterComponent && (
              <Modal.Footer className="algoverse-modal__footer">{renderFooterComponent}</Modal.Footer>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default AlgoverseModalComponent;
